import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Typography } from "@mui/material";
import dayjs from "dayjs";
import { DataGridColumnHeader, TableContainer } from "components/ui";
import { CustomLoader, ReadMore, SmartImage } from "components/atoms";
import GlobalPagination from "utils/GlobalPagination";
import { CharacterName } from "utils/firstCharacterName";
import { GetEventCheckInAction } from "store/middlewares/dataManagement/eventlisting";

// Routes:
//   /user/checkin/:id                       <EventCheckInListing type="user" />   -> API gets userId
//   /data-management/event-listing/checkin/:id  <EventCheckInListing type="event" /> -> API gets eventId
function EventCheckInListing({ type }) {
  const dispatch = useDispatch();
  const { id } = useParams();
  const userId = type === "user" ? id : null;
  const eventId = type === "event" ? id : null;

  const { eventCheckIn, eventCheckInLoading } = useSelector(
    (state) => state.eventlisting,
  );
  const [filterObj, setFilterObj] = useState(null);

  useEffect(() => {
    setFilterObj({
      page: 1,
      pageSize: GlobalPagination.pageSize,
      ...(userId && { userId }),
      ...(eventId && { eventId }),
    });
  }, [userId, eventId]);

  useEffect(() => {
    if (filterObj) dispatch(GetEventCheckInAction(filterObj));
  }, [dispatch, filterObj]);

  const columns = [
    {
      id: "No.",
      accessorFn: (row) => row.id,
      header: ({ column }) => (
        <div className="flex justify-center">
          <DataGridColumnHeader title="No." column={column} />
        </div>
      ),
      enableColumnFilter: false,
      enableSorting: false,
      size: 50,
      cell: ({ row }) => (
        <div className="flex justify-center">
          <Typography variant="p">
            {eventCheckIn?.pageSize * (eventCheckIn?.page - 1) + row.index + 1}
          </Typography>
        </div>
      ),
    },
    // Show the user column only on event-wise listing
    !userId && {
      id: "full_name",
      accessorFn: (row) => row.user?.full_name,
      header: ({ column }) => (
        <DataGridColumnHeader title="User" column={column} />
      ),
      enableSorting: true,
      enableColumnFilter: false,
      size: 250,
      cell: ({ row }) => {
        const u = row.original.user;
        return (
          <div className="flex gap-2 items-center">
            <div className="size-10 rounded-full overflow-hidden flex-shrink-0">
              <SmartImage
                src={u?.profile_url}
                alt="User"
                className="object-cover w-10 h-10 rounded-full"
                fallback={
                  <div className="size-10 bg-[#d3e6ff] rounded-full flex justify-center items-center">
                    <span className="text-[#2563eb] text-[16px]">
                      {CharacterName(u?.full_name)}
                    </span>
                  </div>
                }
              />
            </div>
            <a
              href={`/user/details/${u?.id}`}
              className="font-normal capitalize break-all hover:text-[#2563eb]"
            >
              <ReadMore comment={u?.full_name ?? "N/A"} maxChar={40} />
            </a>
          </div>
        );
      },
    },
    !userId && {
      id: "email",
      accessorFn: (row) => row.user?.email,
      header: ({ column }) => (
        <DataGridColumnHeader title="Email" column={column} />
      ),
      enableSorting: true,
      enableColumnFilter: false,
      size: 200,
      cell: ({ row }) => (
        <span className="font-normal break-all">
          {row.original.user?.email ?? "N/A"}
        </span>
      ),
    },
    // Show the event columns only on user-wise listing
    !eventId && {
      id: "event",
      accessorFn: (row) => row.event?.name,
      header: ({ column }) => (
        <DataGridColumnHeader title="Event" column={column} />
      ),
      enableSorting: true,
      enableColumnFilter: false,
      size: 220,
      cell: ({ row }) => (
        <a
          href={`/data-management/event-listing/${row.original.event?.id}`}
          className="font-normal capitalize break-all hover:text-[#2563eb]"
        >
          <ReadMore comment={row.original.event?.name ?? "N/A"} maxChar={40} />
        </a>
      ),
    },
    !eventId && {
      id: "date",
      accessorFn: (row) => row.event?.date,
      header: ({ column }) => (
        <DataGridColumnHeader title="Event Date" column={column} />
      ),
      enableSorting: true,
      enableColumnFilter: false,
      size: 150,
      cell: ({ row }) => (
        <span className="font-normal">
          {row.original.event?.date
            ? dayjs(row.original.event.date).format("DD/MM/YYYY")
            : "N/A"}
        </span>
      ),
    },
    {
      id: "checked_in_at",
      accessorFn: (row) => row.checked_in_at,
      header: ({ column }) => (
        <DataGridColumnHeader title="Checked In At" column={column} />
      ),
      enableSorting: true,
      enableColumnFilter: false,
      size: 200,
      cell: ({ row }) => (
        <span className="font-normal">
          {row.original.checked_in_at
            ? dayjs(row.original.checked_in_at).format("DD/MM/YYYY hh:mm A")
            : "N/A"}
        </span>
      ),
    },
  ].filter(Boolean);

  return (
    <div className="Vehicle-brand-page">
      {!eventCheckInLoading ? (
        <div className="mt-4">
          <TableContainer
            columns={columns}
            isGlobalFilter={false}
            isPagination={false}
            hasSearch={true}
            onSearch={(val) => {
              setFilterObj({
                ...filterObj,
                page: 1,
                search: val === "" ? null : val.trim(),
              });
            }}
            onUpdatePagination={(size, page) =>
              setFilterObj({ ...filterObj, page, pageSize: size })
            }
            paginationState={{
              pageIndex: filterObj?.page - 1,
              pageSize: filterObj?.pageSize,
            }}
            recordCount={eventCheckIn?.total}
            data={eventCheckIn?.result ?? []}
            title="check-in "
            totalPages={eventCheckIn?.totalPages}
            customSorting={true}
            onUpdateSort={(val) => {
              if (val.length > 0) {
                const t = val[0];
                setFilterObj({
                  ...filterObj,
                  sortOn: t.id,
                  sortBy: t.desc ? "desc" : "asc",
                  page: 1,
                });
              } else {
                setFilterObj({ ...filterObj, page: 1, sortOn: null, sortBy: null });
              }
            }}
          />
        </div>
      ) : (
        <CustomLoader />
      )}
    </div>
  );
}

export default EventCheckInListing;